// LuviaRx — Admin read-only order detail view (items, discounts, shipping,
// customer, payment and full timeline).
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { statusPillClass, statusLabel } from "../../data/orderStatus";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
}) : null);

const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="pt-5 first:pt-0">
    <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-2">{title}</p>
    {children}
  </section>
);

const Line = ({ label, value, accent, strong, testId }) => (
  <div data-testid={testId} className={`flex justify-between gap-4 text-sm ${strong ? "font-display font-bold text-base text-charcoal-600" : "text-charcoal-500"}`}>
    <span>{label}</span>
    <span className={accent ? "text-[#8B2252] font-medium" : ""}>{value}</span>
  </div>
);

const discountLines = (o) => {
  const rows = [];
  if (o.seasonal_promo?.amount > 0)
    rows.push([`${o.seasonal_promo.name} (${o.seasonal_promo.pct}% off)`, o.seasonal_promo.amount, "seasonal"]);
  if (o.volume_discount_amount > 0)
    rows.push([`Volume discount (${o.volume_discount_pct}% off)`, o.volume_discount_amount, "volume"]);
  (o.promo_codes || []).forEach((c) => rows.push([
    `Code ${c.code} (${c.type === "amount" ? money(c.amount_off) : `${c.pct}%`} off)`, c.discount_amount, `code-${c.code}`,
  ]));
  if (!(o.promo_codes || []).length && o.promo_discount_amount > 0)
    rows.push([`Code ${o.promo_code}`, o.promo_discount_amount, "code"]);
  if (o.personal_discount_amount > 0)
    rows.push([`${o.personal_discount_label || "Personal discount"} (${o.personal_discount_pct}% off)`, o.personal_discount_amount, "personal"]);
  if (o.zelle_discount_amount > 0)
    rows.push([`Zelle payment (${o.zelle_discount_pct}% off)`, o.zelle_discount_amount, "zelle"]);
  return rows;
};

const timeline = (o) => [
  ["Order created", o.created_at],
  ["Customer confirmed payment sent", o.payment_acknowledged_at],
  ["Payment confirmed", o.payment_confirmed_at],
  ["Label purchased", o.easypost_label_purchased_at],
  ["Shipped", o.shipped_at],
  ["Refunded", o.refunded_at],
  ["Deleted", o.deleted_at],
  ["Last updated", o.updated_at],
].filter(([, t]) => t);

export default function OrderDetailModal({ order, open, onOpenChange }) {
  if (!order) return null;
  const n = order.order_number;
  const discounts = discountLines(order);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`admin-order-detail-${n}`} className="max-w-2xl max-h-[88vh] overflow-y-auto bg-[#FDFAF9]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#8B2252]">{n}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className={`text-[11px] font-medium px-3 py-1 rounded-full uppercase tracking-wider ${statusPillClass(order.status)}`}>
                {statusLabel(order.status)}
              </span>
              <span className="text-xs text-charcoal-400">
                {(order.payment_method || "—").toUpperCase()}
                {order.stripe_payment_status ? ` · ${order.stripe_payment_status}` : ""}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="divide-y divide-charcoal-100/70 [&>section]:pb-5">
          <Section title="Customer" testId={`admin-order-detail-customer-${n}`}>
            <p className="text-sm font-medium text-charcoal-600">{order.customer_name || "—"}</p>
            <p className="text-sm text-charcoal-400 break-all">{order.customer_email}</p>
            {order.shipping?.phone && <p className="text-sm text-charcoal-400">{order.shipping.phone}</p>}
          </Section>

          <Section title="Items" testId={`admin-order-detail-items-${n}`}>
            <div className="space-y-1.5">
              {order.items.map((it) => (
                <div key={`${it.product_id}-${it.variant_id}`} className="flex justify-between gap-4 text-sm text-charcoal-500">
                  <span>{it.product_name} · {it.variant_label} <span className="text-charcoal-400">× {it.quantity} @ {money(it.unit_price)}</span></span>
                  <span>{money(it.line_total)}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Pricing" testId={`admin-order-detail-pricing-${n}`}>
            <div className="space-y-1.5">
              <Line label="Subtotal" value={money(order.subtotal)} />
              {discounts.map(([label, amt, key]) => (
                <Line key={key} testId={`admin-order-detail-discount-${key}-${n}`} label={label} value={`−${money(amt)}`} accent />
              ))}
              <Line label={`Shipping${order.shipping_method_label ? ` · ${order.shipping_method_label}` : ""}`}
                value={order.shipping_method ? money(order.shipping_total) : "—"} />
              {order.refunded_amount > 0 && <Line label="Refunded" value={`−${money(order.refunded_amount)}`} accent />}
              <div className="pt-2">
                <Line testId={`admin-order-detail-total-${n}`} label="Total" value={money(order.total)} strong />
              </div>
              {typeof order.net_revenue === "number" && (
                <Line label="Net revenue" value={money(order.net_revenue)} />
              )}
            </div>
          </Section>

          <Section title="Shipping" testId={`admin-order-detail-shipping-${n}`}>
            {order.shipping ? (
              <div className="text-sm text-charcoal-500 leading-relaxed">
                <p>{order.shipping.first_name} {order.shipping.last_name}</p>
                <p>{order.shipping.address1}{order.shipping.address2 ? `, ${order.shipping.address2}` : ""}</p>
                <p>{order.shipping.city}, {order.shipping.state} {order.shipping.zip_code}</p>
                <p className="text-charcoal-400">{order.shipping_carrier_label} · {order.shipping_estimated_days}</p>
                {order.tracking_number && (
                  <p className="mt-1">
                    {order.carrier} · <span className="select-all">{order.tracking_number}</span>
                    {order.tracking_url && (
                      <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-[#8B2252] hover:underline">Track</a>
                    )}
                  </p>
                )}
              </div>
            ) : <p className="text-sm text-charcoal-400 italic">Not provided yet</p>}
          </Section>

          <Section title="Timeline" testId={`admin-order-detail-timeline-${n}`}>
            <ol className="space-y-1.5">
              {timeline(order).map(([label, t]) => (
                <li key={label} className="flex justify-between gap-4 text-sm text-charcoal-500">
                  <span>{label}</span><span className="text-charcoal-400">{fmt(t)}</span>
                </li>
              ))}
            </ol>
            {order.refund_reason && <p className="mt-2 text-xs text-charcoal-400">Refund reason: {order.refund_reason}</p>}
            {order.manual_payment_note && <p className="mt-1 text-xs text-charcoal-400">Payment note: {order.manual_payment_note}</p>}
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
